import { useState, useEffect, useRef } from 'react'

/**
 * ChatShortcuts Component
 * Displays recommended shortcut phrases based on chat history
 * and tracks usage for analytics.
 */
export default function ChatShortcuts({ onSelect, mode = 'chat', refreshTrigger = 0, onShortcutsAvailable }) {
  const [shortcuts, setShortcuts] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const refreshTimerRef = useRef(null)
  const MAX_SHORTCUT_WORDS = 10

  function clipWords(text, maxWords = MAX_SHORTCUT_WORDS) {
    const normalized = String(text || '').trim().replace(/\s+/g, ' ')
    if (!normalized) return ''
    const words = normalized.split(' ')
    return words.length > maxWords ? words.slice(0, maxWords).join(' ') : normalized
  }

  // Notify parent when shortcuts are resolved (available or none) so composer can collapse when empty
  useEffect(() => {
    if (mode !== 'chat' || !onShortcutsAvailable) return
    if (loading && shortcuts.length === 0) return // still loading
    onShortcutsAvailable(shortcuts.length > 0)
  }, [mode, loading, shortcuts.length, onShortcutsAvailable])

  // Load shortcuts on mount and when refreshTrigger changes
  useEffect(() => {
    if (mode === 'chat') {
      loadShortcuts()
    }
  }, [mode, refreshTrigger])

  // Set up periodic refresh (every 5 minutes)
  useEffect(() => {
    if (mode === 'chat') {
      refreshTimerRef.current = setInterval(() => {
        loadShortcuts()
      }, 5 * 60 * 1000) // 5 minutes

      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current)
        }
      }
    }
  }, [mode])

  // Only show shortcuts in chat mode
  if (mode !== 'chat') {
    return null
  }

  async function loadShortcuts(minFrequency = 2) {
    setLoading(true)
    setError('')
    try {
      // Try with the specified min_frequency first
      let res = await fetch(`/chat/shortcuts?days=30&max_shortcuts=5&min_frequency=${minFrequency}`)
      
      // Check if response is JSON before parsing
      const contentType = res.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error(`Expected JSON but got ${contentType || 'unknown content type'}`)
      }
      
      let data = await res.json()
      
      // If no shortcuts found with min_frequency=2, try with min_frequency=1
      if ((!res.ok || !data.ok || !data.shortcuts || data.shortcuts.length === 0) && minFrequency === 2) {
        res = await fetch('/chat/shortcuts?days=30&max_shortcuts=5&min_frequency=1')
        const fallbackContentType = res.headers.get('content-type')
        if (!fallbackContentType || !fallbackContentType.includes('application/json')) {
          throw new Error(`Expected JSON but got ${fallbackContentType || 'unknown content type'}`)
        }
        data = await res.json()
      }
      
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`)
      if (data.ok && data.shortcuts && data.shortcuts.length > 0) {
        // Only update if we got new shortcuts
        setShortcuts(data.shortcuts)
      }
      // Don't clear shortcuts if refresh returns empty - keep existing ones
      // This prevents shortcuts from disappearing during refresh
    } catch (e) {
      setError('Failed to load shortcuts')
      console.error('Error loading shortcuts:', e)
      // Don't clear shortcuts on error - keep existing ones visible
    } finally {
      setLoading(false)
    }
  }

  async function handleShortcutClick(phrase) {
    // Shortcut usage is now tracked automatically when the query is sent
    // (via shortcut_phrase in the request body to /ask endpoint)
    // No need to make a separate API call
    
    // Call parent handler
    if (onSelect) {
      onSelect(clipWords(phrase))
    }
  }

  if (loading && shortcuts.length === 0) {
    return (
      <div className="chat-shortcuts">
        <div className="chat-shortcuts-loading">
          <i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Loading shortcuts...
        </div>
      </div>
    )
  }

  if (error && shortcuts.length === 0) {
    return null // Don't show error, just don't display shortcuts
  }

  if (shortcuts.length === 0) {
    return null
  }

  return (
    <div className="chat-shortcuts">
      <div className="suggestion-chips-list suggestion-chips-list--wrap">
        {shortcuts.map((shortcut, i) => {
          const conciseDisplay = clipWords(shortcut.display || shortcut.phrase)
          return (
            <button
              key={i}
              onClick={() => handleShortcutClick(shortcut.phrase || conciseDisplay)}
              className="secondary suggestion-chip suggestion-chip--brand"
              title={conciseDisplay}
              aria-label={`Use shortcut: ${conciseDisplay}`}
            >
              {conciseDisplay}
            </button>
          )
        })}
      </div>
    </div>
  )
}

